const SENSITIVE_KEY_PATTERN = /token|secret|password|auth|bearer|jwt|cookie|cardholder|cvv|cvc|pan/i;
const MAX_STRING_LENGTH = 4096;
const MAX_DEPTH = 4;
const REDACTED = '[REDACTED]';

type SanitizedValue = string | number | boolean;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const truncate = (value: string): string =>
	value.length > MAX_STRING_LENGTH ? value.slice(0, MAX_STRING_LENGTH) : value;

const scrubValue = (value: unknown, depth: number): unknown => {
	if (value === null || value === undefined) return undefined;
	if (typeof value === 'string') return truncate(value);
	if (typeof value === 'number' || typeof value === 'boolean') return value;
	if (depth >= MAX_DEPTH) return undefined;

	if (Array.isArray(value)) {
		return value.map((item) => scrubValue(item, depth + 1)).filter((v) => v !== undefined);
	}

	if (isPlainObject(value)) {
		const out: Record<string, unknown> = {};
		Object.entries(value).forEach(([key, nested]) => {
			if (SENSITIVE_KEY_PATTERN.test(key)) {
				out[key] = REDACTED;
				return;
			}
			const scrubbed = scrubValue(nested, depth + 1);
			if (scrubbed !== undefined) out[key] = scrubbed;
		});
		return out;
	}

	return undefined;
};

const stringifyScrubbed = (value: unknown): string | null => {
	try {
		const json = JSON.stringify(value);
		return json !== undefined ? truncate(json) : null;
	} catch {
		return null;
	}
};

/**
 * Shapes arbitrary caller-supplied context for safe forwarding to New Relic.
 * Recursively replaces values of keys that look like secrets at any depth
 * with `[REDACTED]` (preserves the field name so consumers can spot
 * accidentally forwarded sensitive payloads in NR and so the sanitizer's
 * action is visible), truncates large strings, and flattens nested objects
 * to JSON strings (NR attribute values must be scalars). Depth-limited so
 * circular references can't blow the stack.
 */
export const sanitizeAttributes = (input: unknown): Record<string, SanitizedValue> => {
	if (!isPlainObject(input)) return {};

	const output: Record<string, SanitizedValue> = {};

	Object.entries(input).forEach(([key, value]) => {
		if (SENSITIVE_KEY_PATTERN.test(key)) {
			output[key] = REDACTED;
			return;
		}

		const scrubbed = scrubValue(value, 1);
		if (scrubbed === undefined) return;

		if (typeof scrubbed === 'string' || typeof scrubbed === 'number' || typeof scrubbed === 'boolean') {
			output[key] = scrubbed;
			return;
		}

		const stringified = stringifyScrubbed(scrubbed);
		if (stringified !== null) output[key] = stringified;
	});

	return output;
};
